namespace $.$$ {
	type Type = 'month' | 'slice' | 'auto'
	type ID = [site_id: string, stat_id: number]
	type Stat = { type?: Type } & ({ blowing: keyof $exso_sheets_blowing } | { shift: keyof $exso_sheets_shift })
	type Key = 'blowing' | 'shift' | 'plan'

	export class $exso_sheets_dashboard extends $.$exso_sheets_dashboard {
		@$mol_mem
		cards() {
			return this.stats().flatMap((s, i) => ('plan' in s ? [this.Card(i)] : []))
		}

		@$mol_mem
		sites_list() {
			return this.sites().map(s => this.Site(s))
		}
		site_name(id: string) {
			return id
		}
		site_meta(id: string) {
			const shifts = this.data_site([id, 'shift', 'auto'])
			return super
				.site_meta(id)
				.replace('{d}', this.$.$exso_sheets.unique([shifts, 'device']).length.toString())
				.replace('{s}', shifts.length.toString())
		}
		stats() {
			return super.stats() as {
				title?: string
				icon?: keyof $
				items: Stat[]
				plan?: keyof $exso_sheets_plan
			}[]
		}
		@$mol_mem_key
		site_progresses(id: string) {
			return this.stats().flatMap((s, i) => ('plan' in s ? [this.Site_progress([id, i])] : []))
		}

		Icon(icon?: keyof $) {
			if (!icon) return null
			return new this.$[icon]()
		}

		@$mol_mem_key
		stat(id: number) {
			return this.stats()[id]
		}

		stat_value(id: number) {
			const stat = this.stat(id).items.find(i => 'blowing' in i || 'shift' in i)
			if (!stat) return super.stat_value(id)
			const [key, column] = Object.entries(stat)[0]
			const data = key === 'shift' ? this.shifts() : this.blowings()
			return this.data(key as Key).value([data, column])
		}
		stat_item_title(value: Stat) {
			const [key, column] = Object.entries(value)[0]
			const columns = this.data(key as Key).columns()
			return columns[column].title
		}

		@$mol_mem_key
		stat_title(id: number) {
			const stat = this.stat(id)
			if (stat.title) return stat.title
			const value = stat.items.find(i => 'blowing' in i || 'shift' in i)
			if (!value) return super.stat_title(id)
			return this.stat_item_title(value)
		}

		Stat_icon(id: number) {
			return this.Icon(this.stat(id).icon)
		}

		@$mol_mem_key
		Site_progress_icon([site_id, stat_id]: ID) {
			return this.Stat_icon(stat_id)
		}

		Site_progress(id: ID) {
			const progress = this.site_progress(id)
			if (progress === undefined) return null as any
			return super.Site_progress(id)
		}

		@$mol_mem_key
		site_progress([site_id, stat_id]: ID) {
			const key = this.stat(stat_id).plan
			if (!key) return
			const plan = this.site_plan_to_date([site_id, this.now().toString('YYYY-MM-DD')])?.[key]
			if (!plan) return
			const fact = this.site_stat_val([site_id, stat_id, 0, 'raw'])
			return fact / plan
		}
		@$mol_mem_key
		site_stats(id: string) {
			return this.stats().map((_, i) => this.Site_stat([id, i]))
		}

		site_stat_value([site_id, stat_id, item_id]: [string, number, number]) {
			return this.site_stat_val([site_id, stat_id, item_id, 'full'])
		}

		now() {
			const today = super.date_moment()
			const now = this.date_moment()
			if (today.month === now.month && today.year === now.year) return now
			return this.month_last_day()
		}
		@$mol_mem
		month_last_day() {
			const now = this.date_moment()
			return new $mol_time_moment(new Date(now.year!, now.month! + 1, 0))
		}
		@$mol_mem_key
		site_plan_actual(site_id: string) {
			const now = this.now()
			const plans = this.data_site([site_id, 'plan', 'month'])
			return plans.find(item => item.date.valueOf() > now.valueOf())
		}
		
		site_plan_near(site_id: string) {
			const plans = this.data_site([site_id, "plan", "month"])
			return plans[plans.length - 1]
		}
		@$mol_mem_key
		site_plan_items([site_id, stat_id]: ID) {
			const key = this.stat(stat_id).plan
			if (!key) return []
			const actual = this.site_plan_actual(site_id)
			if (!actual) {
				const near = this.site_plan_near(site_id)
				if (!near) return []
				return [this.Site_plan_item([site_id, near.date_str, key])]
			}
			const now = this.now()
			return Array.from(new Set([now, actual.date, this.month_last_day()])).flatMap(date => {
				const id = [site_id, date.toString('YYYY-MM-DD'), key] as const
				if (!this.site_plan_value(id)) return []
				return [this.Site_plan_item(id)]
			})
		}
		@$mol_mem_key
		site_plan_to_date([site_id, date]: [string, string]) {
			const found = this.data_site([site_id, 'plan', 'month']).find(
				p => p.place === site_id && p.date_str === date,
			)
			if (found) return found
			const actual = this.site_plan_actual(site_id)
			if (!actual) return this.site_plan_near(site_id)
			const factor = new Date(date).getDate() / (actual.date.day! + 1)
			if (factor > 1) return
			return {
				blast: actual['blast'] * factor,
				burnt: actual['burnt'] * factor,
				date: null as any,
				date_str: date,
				place: site_id,
			}
		}
		@$mol_mem_key
		site_plan_value([site_id, date, key]: readonly [string, string, keyof $exso_sheets_plan]) {
			let plan = this.site_plan_to_date([site_id, date])
			if (!plan) return ''
			return this.$.$exso_sheets_plans.value([[plan], key])
		}
		@$mol_mem_key
		site_plan_label([site_id, date, key]: [string, string, string]) {
			return super.site_plan_label('') + ` (${this.$.$exso_format.day(new Date(date))}):`
		}
		@$mol_mem_key
		site_stat_items([site_id, stat_id]: ID) {
			return this.stat(stat_id).items.flatMap((s, i) => [this.Site_stat_item([site_id, stat_id, i])])
		}
		@$mol_mem_key
		site_stat_label([_, stat_id, item_id]: [string, number, number]) {
			const stat = this.stat(stat_id)
			if ('plan' in stat) return this.fact_label() + ':'
			if (item_id === 0) return ''
			const label = this.stat_item_title(stat.items[item_id])
			const root = this.stat_title(stat_id)
			return label.replace(root, '').trim() + ':'
		}
		site_stat_title([, id]: ID) {
			return this.stat_title(id)
		}
		Site_stat_icon([, id]: ID) {
			return this.Stat_icon(id)
		}
		stat_type([, id]: ID) {
			return 'plan' in this.stat(id) ? 'main' : ''
		}

		@$mol_mem_key
		site_stat_val<T extends 'raw' | 'full'>([site_id, stat_id, item_id, t]: [
			string,
			number,
			number,
			T,
		]): T extends 'raw' ? number : string {
			const stat = this.stat(stat_id).items[item_id]
			if (!stat) return t === 'raw' ? (0 as any) : ('-' as any)
			const key = (['blowing', 'shift'] satisfies Key[]).find(k => k in stat)
			if (!key) return t === 'raw' ? (0 as any) : ('-' as any)
			const data = this.data_site([site_id, key, stat.type ?? 'auto']) as any
			const method = t === 'raw' ? 'value_raw' : 'value'
			return this.data(key)[method]([data, (stat as any)[key]])
		}
		site_progress_type(id: ID) {
			const percent = this.site_progress(id)
			if (percent === undefined) return null as any
			if (percent >= 1) return 'high'
			if (percent >= 0.8) return 'medium'
			return 'low'
		}
		site_progress_percent(id: ID) {
			const value = this.site_progress(id)
			if (value === undefined) return '-'
			return this.$.$exso_format.percent(value)
		}
		site_progress_style(id: ID) {
			const value = this.site_progress(id)
			if (value === undefined) return '0'
			return `${value * 100}%`
		}

		@$mol_mem
		sites() {
			const shifts = this.$.$exso_sheets.unique([this.$.$exso_sheets_shifts.list(), 'place'])
			const blowings = this.$.$exso_sheets.unique([this.$.$exso_sheets_blowings.list(), 'place'])
			return Array.from(new Set(shifts.concat(blowings)))
				.filter(
					s =>
						this.data_site([s, 'shift', 'month']).length +
							this.data_site([s, 'plan', 'month']).length +
							this.data_site([s, 'blowing', 'month']).length >
						0,
				)
				.sort(
					(a, b) =>
						this.data_site([b, 'shift', 'month']).length - this.data_site([a, 'shift', 'month']).length,
				)
		}

		data(key: Key) {
			return (this.$ as any)[
				{ blowing: '$exso_sheets_blowings', plan: '$exso_sheets_plans', shift: '$exso_sheets_shifts' }[key]
			]
		}
		@$mol_mem_key
		data_site<K extends Key>([id, key, type]: [string, K, Type]): K extends 'blowing'
			? $exso_sheets_blowing[]
			: K extends 'plan'
				? $exso_sheets_plan[]
				: K extends 'shift'
					? $exso_sheets_shift[]
					: never {
			return Object.fromEntries(
				Object.entries({
					shift: {
						month: () => this.month_shifts(),
						auto: () => this.shifts(),
						slice: () => this.slice_shifts(),
					}[type],
					blowing: () => this.blowings(),
					plan: () => this.plans(),
				}).map(([k, v]) => [k, () => v().filter(({ place }) => place === id)]),
			)[key]() as any
		}
	}
	export class $exso_sheets_dashboard_row extends $.$exso_sheets_dashboard_row {
		Label() {
			return this.label() ? super.Label() : (null as any)
		}
	}
}
